<template>
    <div class="card-body d-flex justify-content-between" v-if="searchProps">
        <slot name="header">
            <div class="search-block">
                <InputGroup style="width: 250px">
                    <template #prepend>
                        <span class="input-group-text"><i class="icon-search4"></i></span>
                    </template>
                    <Input v-model="search.value" type="text" :placeholder="translate('labels.search')"/>
                </InputGroup>
            </div>
            <InputGroup style="width: 200px;">
                <template #prepend>
                    <span class="input-group-text">{{ translate('components.datatable.show') }}:</span>
                </template>
                <Select v-model="length" :options="lengthOptions" optionKey="key" optionValue="key"/>
            </InputGroup>
        </slot>
    </div>
    <div class="table-responsive" style="overflow-y: auto">
        <table class="table table-bordered table-striped table-hover" style="width: 100%; border-collapse: collapse;">
            <thead>
                <tr>
                    <th width="50px" v-if="checkBoxProps">
                        <Checkbox
                            :modelValue="selectedIds.length === rows.length && rows.length > 0"
                            @update:modelValue="selectAllValues"
                        />
                    </th>
                    <th
                        v-for="(column, index) in columns"
                        :class="{
                            'sorting' : getColumnOrdering(index) === null && column.orderable === true,
                            'sorting_desc' : getColumnOrdering(index) === 'desc' && column.orderable === true,
                            'sorting_asc' : getColumnOrdering(index) === 'asc' && column.orderable === true,
                        }"
                        @click="changeOrdering(index, column.orderable)"
                        :style="{width: column.width ?? 'auto'}"
                    >{{ column.field}}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="content.rowForCreate">
                    <slot :name="'rowForCreate'"></slot>
                </tr>
                <template v-if="rows.length > 0">
                    <tr v-for="(row, index) in rows">
                        <td v-if="checkBoxProps" ><Checkbox
                            :modelValue="selectedIds.includes(row[identifier])"
                            @update:modelValue="newValue => selectValue(row[identifier], newValue)"
                            class="no-cursor-change"
                        /></td>
                        <td v-for="(column) in columns" @click="selectRow(index)" class="no-cursor-change" :style="{'min-width': column.width ?? 'auto'}">
                            <slot v-if="row.custom_fields" :name="'row-' + column.name" v-bind:row="row">{{ row[column.name] ?? row.custom_fields[column.name] }}</slot>
                            <slot v-else :name="'row-' + column.name" v-bind:row="row">{{ row[column.name] }}</slot>
                        </td>
                        <td class="d-none">
                            <a :id="generateRowId(index)" :href="row.edit_link"></a>
                        </td>
                    </tr>
                </template>
                <tr v-else>
                    <td :colspan="columns.length + 1" class="empty-table-td no-cursor-change">
                        <slot name="empty">{{ translate('table.titles.no_data') }}</slot>
                    </td>
                </tr>
            </tbody>
            <tfoot v-if="content.tFoot">
                <slot name="tFoot"></slot>
            </tfoot>
        </table>
        </div>
    <div class="datatable-footer" v-if="paginationProps">
        <div class="datatable-info">
            <span>{{translate('labels.showing')}} {{ start + 1 }} - {{ start + length }} {{ translate('labels.of') }} {{ filteredCount }} {{ translate('labels.entries') }}</span>
        </div>
        <div class="datatable-pagination" v-if="paginationProps">
            <ul class="pagination pagination-flat align-self-center  pagination-sm">
                <li :class="['page-item', {'disabled' : currentPage <= 1}]" @click="changeCurrencyPage(currentPage - 1)"><a class="page-link legitRipple">←</a></li>
                <li
                    v-for="pageNumber in pagesCount"
                    :class="['page-item', {'active' : pageNumber === currentPage}]"
                    @click="currentPage = pageNumber"
                >
                    <a class="page-link legitRipple" v-if="(pageNumber >= currentPage && pageNumber < currentPage + 5) || (pageNumber > pagesCount - 5 && currentPage > pagesCount - 5)">{{ pageNumber }}</a>
                </li>
                <li :class="['page-item', {'disabled' : currentPage >= pagesCount}]" @click="changeCurrencyPage(currentPage + 1)"><a class="page-link legitRipple">→</a></li>
            </ul>
        </div>
    </div>
</template>

<script>
import Checkbox from '../new/form/checkbox/AppCheckbox.vue';
import Input from '../new/form/Input.vue';
import Select from '../new/form/select/Select.vue';
import InputGroup from '../new/form/InputGroup.vue';

import { httpClient } from '../../services/HttpClient';

export default {
    name: "DataTableComponent",
    props: {
        url : {
            type: String,
            default() {
                return null;
            }
        },
        identifier : {
            type: [String, Number],
            default() {
                return "id";
            }
        },
        redirectProps: {
            type: Boolean,
            default() {
                return false;
            }
        },
        checkBoxProps: {
            type: Boolean,
            default() {
                return false;
            }
        },
        columns : {
            type : Array,
            default() {
                return [
                    {
                        width: "50px",
                        data: "id",
                        name: "id",
                        searchable: true,
                        orderable: true,
                    }
                ];
            }
        },
        ordersProps: {
            type: Array,
            default() {
                return []
            }
        },
        searchProps: {
            default: true
        },
        paginationProps: {
            default: true
        },
        filtersProps: {
            type: Object
        },
        customFields: {
            type: Array
        }
    },
    emits: ['selectRow', 'changeSelectedIds'],
    data() {
        return {
            currentPage: 1,
            draw: 1,
            length: 10,
            lengthOptions: [
                {key : 10, value: 10},
                {key : 25, value: 25},
                {key : 50, value: 50},
                {key : 100, value: 100},
            ],
            search : {
                value : "",
                regex : 0
            },
            orders: this.ordersProps,
            rows: [],
            filteredCount : 0,
            selectedIds : [],
            meta: {}
        }
    },
    computed: {
        pagesCount() {
            if (this.filteredCount <= this.length) {
                return 1;
            }

            return Math.ceil(this.filteredCount / this.length);
        },
        start() {
            return (this.currentPage - 1) * this.length;
        },
        content() {
            return {
                rowForCreate: this.$slots.rowForCreate,
                tFoot: this.$slots.tFoot
            }
        }
    },
    watch: {
        start: function () {
            this.load();
        },
        length: function () {
            this.currentPage = 1;

            this.load();
        },
        orders: function () {
            this.load();
        },
        'search.value' () {
            this.currentPage = 1;

            this.load();
        },
        'selectedIds': {
            handler(newSelectedIds) {
                this.$emit('changeSelectedIds', newSelectedIds)
            }, deep: true
        }
    },
    methods: {
        selectValue(identifier, checked) {
            if (checked) {
                return this.selectedIds.push(identifier)
            }
            this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== identifier);
        },
        selectAllValues(checked) {
            this.selectedIds = checked ? this.rows.map(element => element[this.identifier]) : [];
        },
        async load()
        {
            let stateData = {
                draw: this.draw,
                start: this.start,
                length: this.length,
                search: this.search,
                orders: this.orders,
                columns : this.columns,
            };

            let data = {
                ...stateData,
                ...this.filtersProps,
                custom_fields: this.customFields
            }

            await httpClient.get(this.url, data).then((response) => {
                this.rows = response.data.data;
                this.filteredCount = response.data.meta.filtered_count;
                this.meta = response.data.meta;
            });

            this.selectedIds = []
        },
        changeCurrencyPage(newValue)
        {
            if (newValue > this.pagesCount || newValue < 1) {
                return;
            }

            this.currentPage = newValue;

            this.recalculateStart();
        },
        recalculateStart()
        {
            this.start = (this.currentPage - 1) * this.length;
        },
        getColumnOrdering(columnIndex)
        {
            let column = this.orders.find((order) => order.column === columnIndex);

            return column ? column.dir : null;
        },
        setColumnOrdering(columnIndex, dir)
        {
            if (dir === 'desc') {
                return this.orders.push({
                    column: columnIndex,
                    dir : dir
                })
            }

            if (dir === 'asc') {
                return this.orders = this.orders.map(function (order)
                {
                    if (order.column === columnIndex) {
                        order.dir = dir;
                    }

                    return order;
                })
            }

            return this.orders = this.orders.filter((order) => order.column !== columnIndex);
        },
        changeOrdering(columnIndex, ordering)
        {
            if(ordering) {
                let columnOrdering = this.getColumnOrdering(columnIndex);

                if (columnOrdering === 'desc') {
                    return this.setColumnOrdering(columnIndex, 'asc');
                }

                if (columnOrdering === 'asc') {
                    return this.setColumnOrdering(columnIndex, null);
                }

                return this.setColumnOrdering(columnIndex, 'desc');
            }
        },
        generateRowId(rowIndex)
        {
            return `row-link-${rowIndex}`;
        },
        selectRow(rowIndex)
        {
            if (this.redirectProps) {
                document.getElementById(this.generateRowId(rowIndex)).click();
            }

            this.$emit('selectRow', this.rows[rowIndex]);
        },
    },
    mounted() {
        this.load();
    },
    components: {
        Checkbox,
        Input,
        Select,
        InputGroup
    }
}
</script>

<style scoped>
i.icon-search4{
    font-size: 14px;
}
.search-block {
    display: flex;
    gap: 10px;
}
.table-responsive {
    overflow: inherit;
}

.table-responsive thead tr th {
    position: relative;
}

.table-responsive thead tr th {
    position: relative;
}

.table-responsive thead .sorting_asc:after,
.table-responsive thead .sorting_desc:after,
.table-responsive thead .sorting:before,
.table-responsive thead .sorting:after
{
    opacity: 1;
    font-family: icomoon;
    position: absolute;
    top: 50%;
    right: 1.25rem;
    font-size: .75rem;
    margin-top: -0.375rem;
    display: inline-block;
    line-height: 1;
    -webkit-font-smoothing: antialiased;
}

.table-responsive thead .sorting_asc:after {
    content: "";
}

.table-responsive thead .sorting_desc:after {
    content: "";
}

.table-responsive thead .sorting:after {
    content: "";
    margin-top: -0.625rem;
    opacity: .5;
}

.table-responsive thead .sorting:before {
    content: "";
    margin-top: -0.125rem;
    opacity: .5;
}

tr:hover {
    cursor: pointer;
}

.datatable > .table-responsive > .table-bordered {
    border: none;
}

.table-responsive > .table-bordered td:last-child,
.table-responsive > .table-bordered th:last-child {
    border-right: 0;
}

.table-responsive > .table-bordered td:first-child,
.table-responsive > .table-bordered th:first-child {
    border-left: 0;
}


.datatable-footer {
    border: none;
}

.datatable-footer, .datatable-header {
    padding: 1.25rem;
}

.sidebar {
    width: 100%;
}

.sidebar-expand-md {
    margin: 0;
}

.datatable-selected-list > span {
    float: left;
    margin-right: 10px;
}

.datatable-selected-list ul {
    list-style-type: none; /* Remove bullets */
    padding: 0; /* Remove padding */
    margin: 0; /* Remove margins */
}

.datatable-selected-list ul li{
    margin-right: 5px;
    display: inline-block;
}

.datatable-search {
    float: left;
    position: relative;
}

.datatable-search label {
    float: left;
    line-height: 38px;
    margin: 0px 10px 0px 0px;
}

.datatable-search input {
    outline: 0;
    width: 200px;
    font-size: .8125rem;
    line-height: 38px;
    color: #333;
    background-color: transparent;
    border: 1px solid #ddd;
    border-width: 1px 0;
    border-top-color: transparent;
    transition: all ease-in-out .15s;
    display: initial;
}

.datatable-search:after {
    content: "";
    font-family: icomoon;
    font-size: .75rem;
    display: inline-block;
    position: absolute;
    top: 50%;
    right: 0;
    margin-top: -0.375rem;
    line-height: 1;
    opacity: .5;
    -webkit-font-smoothing: antialiased;
}

.datatable-show {
    float: right;
    display: flex;
}

.datatable-show label{
    float: left;
    line-height: 38px;
    margin: 0px 10px 0px 0px;
}

.datatable-show .select2-block{
    width: 120px;
    outline: 0;
    position: relative;
    display: inline-block;
    vertical-align: middle;
    text-align: left;
}

.datatable-info{
    float: left;
    line-height: 38px;
}

.datatable-pagination {
    float: right;
}

.datatable-pagination {
    float: right;
}

.datatable-pagination .page-item.active .page-link {
    z-index: 1;
    color: #fff;
    background-color: #37474f;
    border-color: #37474f;
}
.page-item.disabled .page-link {
    background-color: initial
}

.empty-table-td{
    text-align: center;
}
.no-cursor-change {
    cursor: default;
}

th {
    white-space: nowrap;
}
</style>
